import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { ContextualMenuItemType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';

import { Department } from '../../../api-client';
import { RouterSettings } from '../../../utils/router';
import { TRANSLATION_KEYS } from './localization/translation-keys';
import { useTopic } from './hooks/useTopic';
import { useClassNames } from './setting-topic-page.classNames';

import { IAIApplicationLayout } from '../../../common/layouts/IAIApplicationLayout';
import { IAIBreadcrumb } from '../../../common/components/IAIBreadcrumb';
import {
    IAIDetailsList,
    IAIContextualMenuColumn,
    IAIDatetimeColumn,
    IAIPreviewColumn,
    IAIStringColumn
} from '../../../common/components/IAITable';
import DeleteDialog from './components/DeleteDialog/delete-dialog';
import FormDrawer from './components/FormDrawer/form-drawer';

const SettingTopicPage: React.FunctionComponent = () => {
    const {
        settingTopicPageContainer
    } = useClassNames();

    const {
        handleTopics,
        topics,
        topicsLoading,
        handleCreateTopic,
        createTopicLoading,
        handleUpdateTopic,
        updateTopicLoading,
        handleDeleteTopic,
        deleteTopicLoading
    } = useTopic();

    useEffect(() => {
        handleTopics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _handleSearchPredicate = (dataItem: Department, searchKey: string) => (
        _.includes(dataItem.displayName?.toLowerCase(), searchKey)
    );

    //#region Form-Management

    const [topicDrawerVisible, { toggle: toggleTopicDrawerVisible }] = useBoolean(false);
    const [deleteTopicVisible, { toggle: toggleDeleteTopicVisible }] = useBoolean(false);
    const [delegateTopic, setDelegateTopic] = useState<Department>();

    const _handleCreateAction = () => {
        setDelegateTopic(undefined);
        toggleTopicDrawerVisible();
    }

    const _handleUpsertAction = (dataItem: Department) => {
        setDelegateTopic(dataItem);
        toggleTopicDrawerVisible();
    }

    const _handleDismissUpsertAction = () => {
        setDelegateTopic(undefined);
        toggleTopicDrawerVisible();
    }

    const _handleDeleteAction = (dataItem: Department) => {
        setDelegateTopic(dataItem);
        toggleDeleteTopicVisible();
    }

    const _handleDismissDeleteAction = () => {
        setDelegateTopic(undefined);
        toggleDeleteTopicVisible();
    }

    const _handleUpsertTopic = (dataItem: Department) => {
        const onUpsertSuccess = () => {
            _handleDismissUpsertAction();

            handleTopics();
        };

        dataItem.id
            ?
            handleUpdateTopic({ id: dataItem.id, topic: dataItem, onSuccess: onUpsertSuccess })
            :
            handleCreateTopic({ topic: dataItem, onSuccess: onUpsertSuccess });
    }

    const _handleDeleteTopic = () => {
        if (delegateTopic?.id) {
            handleDeleteTopic({
                id: delegateTopic.id,
                onSuccess: () => {
                    _handleDismissDeleteAction();

                    handleTopics();
                }
            });
        }
    }

    //#endregion

    const {
        t: translate
    } = useTranslation();

    return (
        <IAIApplicationLayout
            icon={'BullseyeTarget'}
            heading={translate(TRANSLATION_KEYS.Heading)}
            secondaryHeading={translate(TRANSLATION_KEYS.SecondaryHeading)}
        >
            <div className={settingTopicPageContainer}>
                <IAIBreadcrumb
                    items={[
                        {
                            key: RouterSettings.ROUTES_KEYS.IAI_APP_SETTING,
                            text: translate(TRANSLATION_KEYS.BreadcrumbSettingHeading)
                        },
                        {
                            key: RouterSettings.ROUTES_KEYS.IAI_APP_SETTING_TOPIC,
                            text: translate(TRANSLATION_KEYS.Heading),
                            isCurrentItem: true
                        }
                    ]}
                />

                <IAIDetailsList
                    dataSource={topics || []}
                    dataColumns={[
                        {
                            key: 'tpc__preview',
                            fieldName: '',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (dataItem: Department) => (
                                <IAIPreviewColumn
                                    heading={dataItem.displayName}
                                    content={_.map(dataItem.groups, entry => entry.displayName).join(', ')}
                                    tooltip={translate(TRANSLATION_KEYS.ColumnPreviewTooltip)}
                                />
                            )
                        },
                        {
                            key: 'tpc__displayName',
                            fieldName: 'displayName',
                            name: translate(TRANSLATION_KEYS.ColumnTitleLabel),
                            minWidth: 200,
                            isResizable: true,
                            onRender: (dataItem: Department) => (
                                <IAIStringColumn align="start" text={dataItem.displayName} />
                            )
                        },
                        {
                            key: 'tpc__departments',
                            fieldName: '',
                            name: translate(TRANSLATION_KEYS.ColumnGroupLabel),
                            minWidth: 200,
                            isResizable: true,
                            onRender: (dataItem: Department) => (
                                <IAIStringColumn
                                    align="start"
                                    text={_.map(dataItem.groups, entry => entry.displayName).join(', ')}
                                />
                            )
                        },
                        {
                            key: 'tpc__resolutionDays',
                            fieldName: '',
                            name: translate(TRANSLATION_KEYS.ColumnResolutionDaysLabel),
                            minWidth: 110,
                            isResizable: true,
                            onRender: (dataItem: Department) => (
                                <IAIStringColumn
                                    align="start"
                                    text={`${dataItem?.resolutionDays ?? ""}`}
                                />
                            )
                        },
                        {
                            key: 'tpc__createdAt',
                            fieldName: 'createdAt',
                            name: translate(TRANSLATION_KEYS.ColumnCreatedAtLabel),
                            minWidth: 100,
                            maxWidth: 100,
                            onRender: (dataItem: Department) => (
                                <IAIDatetimeColumn align="start" datetime={dataItem.createdAt} />
                            )
                        },
                        {
                            key: 'tpc__actions',
                            fieldName: '',
                            name: '',
                            minWidth: 50,
                            maxWidth: 50,
                            onRender: (dataItem: Department, dataIndex) => (
                                <IAIContextualMenuColumn
                                    menuItems={[
                                        {
                                            key: `menuItem__update_${dataIndex}`,
                                            iconProps: { iconName: 'EditCreate' },
                                            text: translate(TRANSLATION_KEYS.ColumnActionCommandUpdateLabel),
                                            title: translate(TRANSLATION_KEYS.ColumnActionCommandUpdateTooltip),
                                            onClick: () => _handleUpsertAction(dataItem)
                                        },
                                        { key: `menuItem__divider_${dataIndex}`, itemType: ContextualMenuItemType.Divider },
                                        {
                                            key: `menuItem__delete_${dataIndex}`,
                                            iconProps: { iconName: 'Delete' },
                                            text: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteLabel),
                                            title: translate(TRANSLATION_KEYS.ColumnActionCommandDeleteTooltip),
                                            onClick: () => _handleDeleteAction(dataItem)
                                        }
                                    ]}
                                />
                            )
                        }
                    ]}
                    dataLoading={topicsLoading}
                    commands={[
                        {
                            key: '',
                            disabled: topicsLoading,
                            text: translate(TRANSLATION_KEYS.CommandCreateLabel),
                            title: translate(TRANSLATION_KEYS.CommandCreateTooltip),
                            iconProps: { iconName: 'Add' },
                            onClick: _handleCreateAction
                        }
                    ]}
                    emptyContentLabel={translate(TRANSLATION_KEYS.DetailsListEmptyContentLabel)}
                    onItemInvoked={(dataItem: Department) => _handleUpsertAction(dataItem)}
                    onSearchPredicate={_handleSearchPredicate}
                />

                <FormDrawer
                    topic={delegateTopic}
                    disabled={topicsLoading || createTopicLoading || updateTopicLoading}
                    drawerVisible={topicDrawerVisible}
                    onDrawerConfirm={_handleUpsertTopic}
                    onDrawerDismiss={_handleDismissUpsertAction}
                />

                <DeleteDialog
                    disabled={topicsLoading || deleteTopicLoading}
                    modalVisible={deleteTopicVisible}
                    onModalConfirm={_handleDeleteTopic}
                    onModalDismiss={_handleDismissDeleteAction}
                />
            </div>
        </IAIApplicationLayout>
    );
}

export default SettingTopicPage;